import { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';
import { Mail, Phone, Building, Trash2, Eye } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useLeads, useUpdateLead, useDeleteLead } from '@/hooks/useLeads';
import { statusColors } from '@/lib/utils';
import { useTranslation, useLanguage } from '@/features/i18n';
import type { Lead, LeadStatus } from '@/types/database';

export function LeadsPage() {
  const [statusFilter, setStatusFilter] = useState<LeadStatus | undefined>();
  const [page, setPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);

  const { data, isLoading } = useLeads({ status: statusFilter, page });
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const dateLocale = language === 'no' ? nb : enUS;

  const statusOptions = [
    { value: '', label: t.admin.common.allStatuses },
    { value: 'new', label: t.admin.leads.status.new },
    { value: 'contacted', label: t.admin.leads.status.contacted },
    { value: 'qualified', label: t.admin.leads.status.qualified },
    { value: 'proposal', label: t.admin.leads.status.proposal },
    { value: 'won', label: t.admin.leads.status.won },
    { value: 'lost', label: t.admin.leads.status.lost },
  ];

  const getStatusLabel = (status: LeadStatus) => {
    return t.admin.leads.status[status] || status;
  };

  const handleStatusChange = async (leadId: string, newStatus: LeadStatus) => {
    await updateLead.mutateAsync({
      id: leadId,
      status: newStatus,
      ...(newStatus === 'contacted' && { contacted_at: new Date().toISOString() }),
    });
  };

  const handleDelete = async () => {
    if (leadToDelete) {
      await deleteLead.mutateAsync(leadToDelete.id);
      setDeleteModalOpen(false);
      setLeadToDelete(null);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.leads.title} noIndex />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.leads.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t.admin.leads.subtitle}
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Select
                options={statusOptions}
                value={statusFilter || ''}
                onChange={(e) => {
                  setStatusFilter(e.target.value as LeadStatus || undefined);
                  setPage(1);
                }}
                placeholder={t.admin.common.filterByStatus}
              />
            </div>
          </div>
        </Card>

        {/* Leads list */}
        {data?.leads && data.leads.length > 0 ? (
          <div className="space-y-4">
            {data.leads.map((lead, index) => (
              <motion.div
                key={lead.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-neutral-900 dark:text-white">
                          {lead.name}
                        </h3>
                        <Badge className={statusColors[lead.status]}>
                          {getStatusLabel(lead.status)}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                        <a
                          href={`mailto:${lead.email}`}
                          className="flex items-center hover:text-primary-600"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          {lead.email}
                        </a>
                        {lead.phone && (
                          <a
                            href={`tel:${lead.phone}`}
                            className="flex items-center hover:text-primary-600"
                          >
                            <Phone className="w-4 h-4 mr-1" />
                            {lead.phone}
                          </a>
                        )}
                        {lead.company && (
                          <span className="flex items-center">
                            <Building className="w-4 h-4 mr-1" />
                            {lead.company}
                          </span>
                        )}
                      </div>

                      {lead.message && (
                        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">
                          {lead.message}
                        </p>
                      )}

                      <div className="mt-2 text-xs text-neutral-500">
                        {format(new Date(lead.created_at), 'PPP', { locale: dateLocale })}
                        {lead.service_interest && ` • ${t.admin.leads.interestedIn}: ${lead.service_interest}`}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Select
                        options={statusOptions.slice(1)}
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value as LeadStatus)}
                        className="w-40"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLead(lead)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setLeadToDelete(lead);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t.admin.common.previous}
                </Button>
                <span className="flex items-center px-4 text-neutral-600 dark:text-neutral-400">
                  {t.admin.common.pageOf.replace('{page}', String(page)).replace('{total}', String(data.totalPages))}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page === data.totalPages}
                >
                  {t.admin.common.next}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={t.admin.leads.noLeads}
            description={statusFilter ? t.admin.leads.noLeadsFilter : t.admin.leads.noLeadsDesc}
          />
        )}
      </div>

      {/* Lead detail modal */}
      <Modal
        isOpen={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        title={selectedLead?.name || ''}
        size="lg"
      >
        {selectedLead && (
          <div className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-neutral-500">{t.admin.leads.email}</label>
                <p className="text-neutral-900 dark:text-white">{selectedLead.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-500">{t.admin.leads.phone}</label>
                <p className="text-neutral-900 dark:text-white">{selectedLead.phone || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-500">{t.admin.leads.company}</label>
                <p className="text-neutral-900 dark:text-white">{selectedLead.company || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-500">{t.admin.leads.companySize}</label>
                <p className="text-neutral-900 dark:text-white">{selectedLead.company_size || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-500">{t.admin.leads.interestedIn}</label>
                <p className="text-neutral-900 dark:text-white">{selectedLead.service_interest || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-500">{t.admin.blog.status}</label>
                <Badge className={statusColors[selectedLead.status]}>
                  {getStatusLabel(selectedLead.status)}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-500">{t.admin.leads.message}</label>
              <p className="text-neutral-900 dark:text-white whitespace-pre-wrap">
                {selectedLead.message || '-'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-500">{t.admin.common.received}</label>
              <p className="text-neutral-900 dark:text-white">
                {format(new Date(selectedLead.created_at), 'PPPp', { locale: dateLocale })}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t.admin.leads.deleteLead}
      >
        <div className="p-6">
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t.admin.common.confirmDeleteGeneric} {t.admin.common.cannotUndo}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteLead.isPending}>
              {t.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
